import { Drawer, Flex, Group, Stack, Text } from '@mantine/core';
import useRetrieveIntegrations from '@/hooks/api/integrations/useRetrieveIntegrations.ts';
import IntegrationCard from '@/pages/Integrations/components/IntegrationCard.tsx';
import CustomSpinner from '@/components/Spinner.tsx';
import PageHeaderText from '@/components/PageHeaderText.tsx';
import { useMemo, useState } from 'react';
import IntegrationProviderCard from '@/pages/Integrations/components/IntegrationProviderCard.tsx';
import IntegrationLogo from '@/pages/Integrations/components/IntegrationLogo.tsx';
import { IntegrationProvider } from '@/types/integrations.ts';
import { isExternalIntegrationProvider } from '@/utils/providerKinds.ts';

export function IntegrationsPage() {
  const { data: allIntegrations, isPending: loadingIntegrations } = useRetrieveIntegrations();

  const integrations = useMemo(
    () => allIntegrations?.results.filter((p) => isExternalIntegrationProvider(p)) || [],
    [allIntegrations],
  );

  const [selectedIntegration, setSelectedIntegration] = useState<IntegrationProvider | undefined>(
    undefined,
  );

  const activeIntegrations = integrations.filter((p) => p.instances.length > 0);
  const availableIntegrations = integrations.filter((p) => p.instances.length === 0);

  return (
    <>
      <Drawer
        opened={!!selectedIntegration}
        onClose={() => setSelectedIntegration(undefined)}
        title={
          selectedIntegration && (
            <Group gap={'xs'}>
              <IntegrationLogo name={selectedIntegration.name} />
              <Text size={'14px'} fw={'medium'}>
                {selectedIntegration.display_name}
              </Text>
            </Group>
          )
        }
        position="right"
        styles={{
          body: { borderTop: '1px solid #E0E0E0' },
          overlay: {
            backdropFilter: 'blur(2.5px)',
            background: 'rgb(255 255 255 / 70%)',
          },
          content: {
            boxShadow: '0px 0px 15px rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        <Stack></Stack>
        {/* Drawer content */}
      </Drawer>

      <Flex justify={'space-between'} w={'100%'} mb={'50px'}>
        <PageHeaderText title="Integrations" />
      </Flex>
      <Stack gap={60}>
        <Stack gap="xs">
          <Text size={'md'} fw={'500'}>
            Active Integrations
          </Text>
          <Group gap={4}>
            {loadingIntegrations ? (
              <CustomSpinner size={'md'} />
            ) : (
              <>
                {activeIntegrations ? (
                  activeIntegrations.map((provider) => (
                    <IntegrationCard
                      key={provider.name}
                      // integration={i}
                      provider={provider}
                      // setSelectedIntegration={setSelectedIntegration}
                    />
                  ))
                ) : (
                  <Flex w={'100%'} justify={'center'}>
                    <Text mb={'lg'} mt={'sm'} size={'sm'}>
                      No integrations enabled
                    </Text>
                  </Flex>
                )}
              </>
            )}
          </Group>
        </Stack>
        <Stack gap="xs">
          <Text size={'md'} fw={'500'}>
            Connect Integrations
          </Text>
          <Group gap={12}>
            {loadingIntegrations ? (
              <CustomSpinner size={'md'} />
            ) : (
              <>
                {availableIntegrations.map((provider) => (
                  <IntegrationProviderCard
                    provider={provider}
                    key={provider.name}
                    setSelectedIntegration={setSelectedIntegration}
                  />
                ))}
              </>
            )}
          </Group>
        </Stack>
      </Stack>
    </>
  );
}

export default IntegrationsPage;
